import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@repo/o37-ui";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface VDRActivity {
  visits: any[];
  totalVisits: number;
  totalTime: number;
  lastVisit: string | null;
  message?: string;
}

interface VDRActivityPanelProps {
  fundId: string;
}

export default function VDRActivityPanel({ fundId }: VDRActivityPanelProps) {
  const [activity, setActivity] = useState<VDRActivity | null>(null);
  const [loading, setLoading] = useState(true);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadActivity();
  }, [fundId]);

  const loadActivity = async () => {
    try {
      const response = await fetch(`${API_URL}/fundraising/funds/${fundId}/vdr-activity`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setActivity(data);
      }
    } catch (e) {
      console.error('Failed to load VDR activity:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return `${Math.floor(days / 7)}w ago`;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>VDR Activity</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-sm text-neutral-500 text-center">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  if (!activity || activity.message) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>VDR Activity</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-sm text-neutral-400 text-center">
            {activity?.message || 'No VDR linked to this fundraise'}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (activity.totalVisits === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>VDR Activity</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-sm text-neutral-400 text-center">
            No VDR visits from this fund's contacts yet
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>VDR Activity</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-neutral-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-semibold">{activity.totalVisits}</div>
            <div className="text-xs text-neutral-500">Page Views</div>
          </div>
          <div className="bg-neutral-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-semibold">{formatDuration(activity.totalTime)}</div>
            <div className="text-xs text-neutral-500">Time Spent</div>
          </div>
        </div>

        {activity.lastVisit && (
          <div className="text-sm text-neutral-500 text-center">
            Last visit: {formatTimeAgo(activity.lastVisit)}
          </div>
        )}

        {activity.visits.length > 0 && (
          <div className="pt-4 border-t border-neutral-100">
            <h4 className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-2">Recent Activity</h4>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {activity.visits.slice(0, 10).map((visit: any, i: number) => (
                <div key={i} className="flex items-center justify-between text-sm py-1">
                  <div className="flex items-center gap-2">
                    <span className="text-neutral-400">
                      {visit.action_type === 'view_page' && '👁️'}
                      {visit.action_type === 'time_on_page' && '⏱️'}
                      {visit.action_type === 'click_button' && '👆'}
                      {visit.action_type === 'download_file' && '📥'}
                    </span>
                    <span className="text-neutral-700 truncate max-w-[150px]">
                      {visit.path || visit.action_type}
                    </span>
                  </div>
                  <span className="text-neutral-400 text-xs">
                    {formatTimeAgo(visit.created_at)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
